import { type FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';

import MediaCarousel from './carousel/MediaCarousel.jsx';
import StorefrontSectionHeader from './StorefrontSectionHeader.jsx';

import styles from './styles/StorefrontMediaSection.scss';
import { ServerVarsContextConsumer } from '../../sharedComponents/ServerVarsContext.tsx';

import { type StorefrontMediaSection_seller$key } from './__generated__/StorefrontMediaSection_seller.graphql.ts';
import { type StorefrontMediaSection_viewer$key } from './__generated__/StorefrontMediaSection_viewer.graphql.ts';

type Props = {
    seller: StorefrontMediaSection_seller$key;
    viewer: StorefrontMediaSection_viewer$key;
};

const StorefrontMediaSection: FC<Props> = ({ seller: sellerRef, viewer: viewerRef }) => {
    const viewer = useFragment(
        graphql`
            fragment StorefrontMediaSection_viewer on Viewer {
                vimeoStorefrontVideoHLSPlayerFF: featureFlag(
                    feature: "vimeoStorefrontVideoHLSPlayer"
                )
            }
        `,
        viewerRef
    );

    const seller = useFragment(
        graphql`
            fragment StorefrontMediaSection_seller on Seller {
                sellerProfile {
                    company
                }
                storefrontProfile(storefrontId: $storefrontId) {
                    galleryAlbum {
                        mediaList {
                            sortOrder
                            element {
                                ... on Photo {
                                    serviceId
                                    path
                                    caption
                                    mediaType
                                }
                                ... on Video {
                                    serviceId
                                    url
                                    caption
                                    mediaType
                                }
                            }
                        }
                    }
                }
            }
        `,
        sellerRef
    );

    const mediaList = seller?.storefrontProfile?.galleryAlbum?.mediaList;

    if (!mediaList?.length) {
        return null;
    }

    return (
        <div className={styles.section}>
            <StorefrontSectionHeader
                header={
                    <FormattedMessage
                        id="StorefrontMediaSection.header"
                        defaultMessage="More About {company}"
                        values={{ company: seller?.sellerProfile?.company || '' }}
                    />
                }
            />
            <div className={styles.content}>
                <ServerVarsContextConsumer>
                    {({ isMobile }) => (
                        <MediaCarousel
                            useSrcSet
                            mediaList={mediaList}
                            isMobile={isMobile}
                            vimeoStorefrontVideoHLSPlayerFF={
                                !!viewer.vimeoStorefrontVideoHLSPlayerFF
                            }
                        />
                    )}
                </ServerVarsContextConsumer>
            </div>
        </div>
    );
};

export default StorefrontMediaSection;
